// Generated from scripts/share.ts. Rebuild with pnpm scripts:build.
/**
 * Share.
 *
 * One control for sending the page to someone. A phone opens the system share sheet; every other device copies
 * the link, and offers plain links to the networks the merchant chose, which need no script. Which of the two
 * the shopper sees on first paint is decided in CSS from `(pointer: coarse)` (the share-state utility), so
 * nothing swaps when this module connects. The module only handles the clicks and corrects the one case CSS
 * cannot see: a touch device without `navigator.share` copies like any other device, which it marks with
 * `data-share-fallback`.
 *
 * Sharing. `navigator.share` takes the page address and title. Dismissing the sheet rejects with an
 * `AbortError`, which is the shopper's choice and not an error; any other refusal falls back to copying.
 *
 * Copying goes through the clipboard owner (`copy-to-clipboard-component`, scripts/copy-to-clipboard.ts),
 * which says whether the text was copied. The result is announced once in a visually hidden polite region and
 * shown for a moment in a small bubble and by swapping the link icon for a check (`data-copied`,
 * `data-feedback`). The bubble is decoration for the region's text, so it is hidden from assistive technology.
 */
/** How long the confirmation stays on screen. */
const FEEDBACK_MS = 2400;
function isClipboardOwner(element) {
    return element !== null && 'copyToClipboard' in element && typeof element.copyToClipboard === 'function';
}
export class WontioShare extends HTMLElement {
    #lifetime;
    #timer;
    connectedCallback() {
        this.#teardown();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const options = { signal: lifetime.signal };
        this.addEventListener('click', this.#onClick, options);
        const touch = window.matchMedia('(pointer: coarse)');
        const sync = () => {
            this.toggleAttribute('data-share-fallback', touch.matches && typeof navigator.share !== 'function');
        };
        touch.addEventListener('change', sync, options);
        sync();
    }
    disconnectedCallback() {
        this.#teardown();
    }
    #teardown() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        window.clearTimeout(this.#timer);
        this.#timer = undefined;
        this.removeAttribute('data-feedback');
        delete this.dataset.copied;
    }
    #onClick = (event) => {
        if (!(event.target instanceof Element))
            return;
        if (event.target.closest('[data-share-native]'))
            void this.#share();
        else if (event.target.closest('[data-share-copy]'))
            void this.#copy();
    };
    async #share() {
        const url = this.dataset.url;
        if (!url)
            return;
        const data = { url };
        if (this.dataset.title)
            data.title = this.dataset.title;
        try {
            if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) {
                await this.#copy();
                return;
            }
            await navigator.share(data);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError')
                return;
            await this.#copy();
        }
    }
    async #copy() {
        const owner = this.querySelector('copy-to-clipboard-component');
        if (!owner)
            return;
        await customElements.whenDefined('copy-to-clipboard-component');
        this.#confirm(isClipboardOwner(owner) && (await owner.copyToClipboard()));
    }
    /** Says whether the link was copied: once to assistive technology, and for a moment on screen. */
    #confirm(copied) {
        const message = copied ? this.dataset.copiedLabel : this.dataset.failedLabel;
        if (!message)
            return;
        // Clearing first lets a second copy in a row be announced again.
        const status = this.querySelector('[data-share-status]');
        if (status) {
            status.textContent = '';
            window.requestAnimationFrame(() => {
                status.textContent = message;
            });
        }
        const feedback = this.querySelector('[data-share-feedback]');
        if (feedback)
            feedback.textContent = message;
        this.setAttribute('data-feedback', '');
        if (copied)
            this.dataset.copied = '';
        else
            delete this.dataset.copied;
        window.clearTimeout(this.#timer);
        this.#timer = window.setTimeout(() => {
            this.removeAttribute('data-feedback');
            delete this.dataset.copied;
        }, FEEDBACK_MS);
    }
}
if (!customElements.get('wontio-share'))
    customElements.define('wontio-share', WontioShare);
